import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  type AgriculturalLandRecord,
  type ApartmentUnitRecord,
  type FamilyHouseRecord,
  type LandTransactionRecord,
  type OfferListingRecord
} from '../models/advanced.model';

@Injectable({ providedIn: 'root' })
export class AdvancedService {
  private readonly baseUrl = environment.baseUrl;

  constructor(private readonly http: HttpClient) {}

  exportLandTransactions(records: LandTransactionRecord[]): Observable<HttpResponse<Blob>> {
    const formData = new FormData();

    records.forEach((record, index) => {
      const prefix = `Records[${index}]`;
      formData.append(`${prefix}.CadastralArea`, record.cadastralArea.trim());
      this.appendArray(formData, `${prefix}.ParcelNumbers`, record.parcelNumbers);
      this.appendIfPresent(formData, `${prefix}.TotalArea`, record.totalArea);
      this.appendIfPresent(formData, `${prefix}.RealizedPrice`, record.realizedPrice);
      this.appendIfPresent(formData, `${prefix}.PricePerM2`, record.pricePerM2);
      formData.append(`${prefix}.Date`, record.date);
      formData.append(`${prefix}.CaseNumber`, record.caseNumber.trim());
      formData.append(`${prefix}.Source`, record.source.trim());
      formData.append(`${prefix}.LandUseCategory`, record.landUseCategory.trim());
      formData.append(`${prefix}.BuiltStatus`, record.builtStatus.trim());
      this.appendImages(formData, `${prefix}.Images`, record.images);
    });

    return this.postExport(formData, '/Comparative/ExportLandTransactions');
  }

  exportAgriculturalLand(records: AgriculturalLandRecord[]): Observable<HttpResponse<Blob>> {
    const formData = new FormData();

    records.forEach((record, index) => {
      const prefix = `Records[${index}]`;
      formData.append(`${prefix}.CadastralArea`, record.cadastralArea.trim());
      formData.append(`${prefix}.ParcelNumber`, record.parcelNumber.trim());
      formData.append(`${prefix}.LandType`, record.landType.trim());
      this.appendIfPresent(formData, `${prefix}.TotalArea`, record.totalArea);
      this.appendIfPresent(formData, `${prefix}.RealizedPrice`, record.realizedPrice);
      this.appendIfPresent(formData, `${prefix}.PricePerM2`, record.pricePerM2);
      formData.append(`${prefix}.Date`, record.date);
      this.appendIfPresent(formData, `${prefix}.CaseNumber`, record.caseNumber.trim());
      formData.append(`${prefix}.Source`, record.source.trim());
      this.appendImages(formData, `${prefix}.Images`, record.images);
    });

    return this.postExport(formData, '/Comparative/ExportAgriculturalLand');
  }

  exportApartmentUnits(records: ApartmentUnitRecord[]): Observable<HttpResponse<Blob>> {
    const formData = new FormData();

    records.forEach((record, index) => {
      const prefix = `Records[${index}]`;
      formData.append(`${prefix}.CadastralArea`, record.cadastralArea.trim());
      formData.append(`${prefix}.UnitNumber`, record.unitNumber.trim());
      this.appendIfPresent(formData, `${prefix}.FloorArea`, record.floorArea);
      this.appendIfPresent(formData, `${prefix}.RealizedPrice`, record.realizedPrice);
      this.appendIfPresent(formData, `${prefix}.PricePerM2`, record.pricePerM2);
      formData.append(`${prefix}.Date`, record.date);
      formData.append(`${prefix}.CaseNumber`, record.caseNumber.trim());
      formData.append(`${prefix}.Source`, record.source.trim());
      this.appendImages(formData, `${prefix}.Images`, record.images);
    });

    return this.postExport(formData, '/Comparative/ExportApartmentUnits');
  }

  exportFamilyHouses(records: FamilyHouseRecord[]): Observable<HttpResponse<Blob>> {
    const formData = new FormData();

    records.forEach((record, index) => {
      const prefix = `Records[${index}]`;
      formData.append(`${prefix}.CadastralArea`, record.cadastralArea.trim());
      formData.append(`${prefix}.ParcelNumber`, record.parcelNumber.trim());
      this.appendIfPresent(formData, `${prefix}.LandArea`, record.landArea);
      this.appendIfPresent(formData, `${prefix}.BuiltArea`, record.builtArea);
      this.appendIfPresent(formData, `${prefix}.UsableArea`, record.usableArea);
      this.appendIfPresent(formData, `${prefix}.RealizedPrice`, record.realizedPrice);
      this.appendIfPresent(formData, `${prefix}.PricePerM2`, record.pricePerM2);
      formData.append(`${prefix}.Date`, record.date);
      this.appendIfPresent(formData, `${prefix}.CaseNumber`, record.caseNumber.trim());
      formData.append(`${prefix}.Source`, record.source.trim());
      this.appendImages(formData, `${prefix}.Images`, record.images);
    });

    return this.postExport(formData, '/Comparative/ExportFamilyHouses');
  }

  exportOfferListings(records: OfferListingRecord[]): Observable<HttpResponse<Blob>> {
    const formData = new FormData();

    records.forEach((record, index) => {
      const prefix = `Records[${index}]`;
      formData.append(`${prefix}.PropertyType`, record.propertyType.trim());
      formData.append(`${prefix}.Location`, record.location.trim());
      this.appendIfPresent(formData, `${prefix}.LandArea`, record.landArea);
      this.appendIfPresent(formData, `${prefix}.UsableArea`, record.usableArea);
      this.appendIfPresent(formData, `${prefix}.AskingPrice`, record.askingPrice);
      this.appendIfPresent(formData, `${prefix}.PricePerM2`, record.pricePerM2);
      formData.append(`${prefix}.Date`, record.date);
      formData.append(`${prefix}.Website`, record.website.trim());
      this.appendImages(formData, `${prefix}.Images`, record.images);
    });

    return this.postExport(formData, '/Comparative/ExportOfferListings');
  }

  private postExport(formData: FormData, path: string): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.baseUrl}${path}`, formData, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  private appendIfPresent(formData: FormData, key: string, value: number | string | null | undefined): void {
    if (value === null || value === undefined) {
      return;
    }

    const stringValue = typeof value === 'number' ? String(value) : value.trim();
    if (!stringValue) {
      return;
    }

    formData.append(key, stringValue);
  }

  private appendArray(formData: FormData, key: string, values: string[]): void {
    for (const value of values) {
      const trimmed = value.trim();
      if (trimmed) {
        formData.append(key, trimmed);
      }
    }
  }

  private appendImages(formData: FormData, key: string, images: File[]): void {
    for (const image of images) {
      formData.append(key, image, image.name);
    }
  }
}
